import { useEffect, useState } from 'react';

import { ActivateButton } from '../../components/ActivateButton';
import { ContentBand } from '../../components/ContentBand';
import { phraseRepo } from '../../data/repositories';
import { speechService } from '../../core/speech/SpeechService';
import { BodyMap } from './BodyMap';
import { PAIN_MAX, painUtterance } from './notfallData';
import type { Phrase } from '../../core/types';

/**
 * The highest-consequence screen in the app.
 *
 * Three ways to say the same class of thing, because the situations differ: the protected core
 * phrases for the common cases, a 0–10 scale for how bad it is, and a body map for where — which
 * covers the case where the pain is severe enough that typing has stopped being possible.
 *
 * Everything here speaks immediately. There is no confirmation step even when
 * `confirmOnPhraseTap` is on: this is the one surface where a false positive costs less than a
 * delay.
 */

const NOTFALL_CATEGORY = 'cat-notfall';

type Tab = 'kern' | 'schmerz' | 'koerper';

export function Notfall(): React.JSX.Element {
  const [core, setCore] = useState<Phrase[]>([]);
  const [tab, setTab] = useState<Tab>('kern');

  useEffect(() => {
    void phraseRepo.byCategory(NOTFALL_CATEGORY).then(setCore);
  }, []);

  const speak = (text: string): void => {
    void speechService.speak({ text, source: 'phrase', isPhrase: true });
  };

  return (
    <ContentBand grow>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: 'var(--band-gap)' }}>
        <div style={{ display: 'flex', gap: 'var(--key-gap)', flex: '0 0 auto' }}>
          <TabButton id="kern" active={tab} label="Notfall" onPress={setTab} />
          <TabButton id="schmerz" active={tab} label="Schmerzen" onPress={setTab} />
          <TabButton id="koerper" active={tab} label="Wo?" onPress={setTab} />
        </div>

        <div style={{ flex: '1 1 auto', minHeight: 0 }}>
          {tab === 'kern' ? <CoreSet phrases={core} onSpeak={speak} /> : null}
          {tab === 'schmerz' ? <PainScale onSpeak={speak} /> : null}
          {tab === 'koerper' ? <BodyMap onSpeak={speak} /> : null}
        </div>
      </div>
    </ContentBand>
  );
}

function TabButton({
  id,
  active,
  label,
  onPress,
}: {
  id: Tab;
  active: Tab;
  label: string;
  onPress: (tab: Tab) => void;
}): React.JSX.Element {
  const isActive = active === id;
  return (
    <ActivateButton
      id={`notfall-tab:${id}`}
      onActivate={() => onPress(id)}
      pressed={isActive}
      style={{
        flex: '1 1 0',
        minHeight: 'var(--min-target)',
        fontSize: 'var(--font-button)',
        fontWeight: isActive ? 700 : 400,
        background: isActive ? 'var(--fg)' : 'var(--key-bg)',
        color: isActive ? 'var(--bg)' : 'var(--key-fg)',
        border: '3px solid var(--key-border)',
        borderRadius: 'var(--radius)',
      }}
    >
      {label}
    </ActivateButton>
  );
}

function CoreSet({
  phrases,
  onSpeak,
}: {
  phrases: Phrase[];
  onSpeak: (text: string) => void;
}): React.JSX.Element {
  return (
    <div
      data-testid="notfall-core"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridAutoRows: '1fr',
        gap: 'var(--key-gap)',
        height: '100%',
      }}
    >
      {phrases.map((phrase) => (
        <ActivateButton
          key={phrase.id}
          id={`notfall:${phrase.id}`}
          onActivate={() => onSpeak(phrase.text)}
          label={phrase.text}
          style={{
            fontSize: 'var(--font-button)',
            fontWeight: 700,
            background: 'var(--danger)',
            color: 'var(--danger-fg)',
            border: '4px solid var(--fg)',
            // Same cut-corner silhouette as the Notfall button in the bottom bar, so the two
            // read as the same thing without relying on colour (§5.2).
            borderRadius: 0,
            clipPath: 'polygon(8% 0, 100% 0, 100% 92%, 92% 100%, 0 100%, 0 8%)',
          }}
        >
          {phrase.shortLabel ?? phrase.text}
        </ActivateButton>
      ))}
    </div>
  );
}

/**
 * 0–10 in **two rows of six and five**.
 *
 * Eleven targets in one row inside the 60 % band would be 12.3 mm each, below the 16 mm
 * interactive floor. Two rows keeps every step comfortably above it.
 */
function PainScale({ onSpeak }: { onSpeak: (text: string) => void }): React.JSX.Element {
  const levels = Array.from({ length: PAIN_MAX + 1 }, (_, index) => index);
  const rows = [levels.slice(0, 6), levels.slice(6)];

  return (
    <div
      data-testid="pain-scale"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--key-gap)', height: '100%' }}
    >
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} style={{ display: 'flex', gap: 'var(--key-gap)', flex: '1 1 0' }}>
          {row.map((level) => (
            <ActivateButton
              key={level}
              id={`pain:${level}`}
              onActivate={() => onSpeak(painUtterance(level))}
              label={painUtterance(level)}
              style={{
                flex: '1 1 0',
                minWidth: 0,
                fontSize: 'calc(var(--font-output) * 0.7)',
                fontWeight: 700,
                background: 'var(--key-bg)',
                color: 'var(--key-fg)',
                // Thickening border with severity: a second, non-colour channel for the scale.
                border: `${2 + Math.round(level * 0.6)}px solid var(--key-border)`,
                borderRadius: 'var(--radius)',
              }}
            >
              {level}
            </ActivateButton>
          ))}
        </div>
      ))}
    </div>
  );
}
